/**
 * @file   src/Pages/FleetManagement.tsx
 * @brief  Fleet Management listing for pending and approved fleet accounts.
 */
import { React, useEffect, useOutletContext, useState, Container, Table, Tabs, Tab, Button, Modal, Accordion } from '../utils/thirdpartyComponents';
import { AppContextType } from '../interfaces/generalInterface';
import { CarInfo } from '../interfaces/subscriptionInterface';
import { pageURLs } from '../utils/pageUrls';
import SmallLoader from '../components/SmallLoader';
import { MessageToaster } from '../utils/toastUtil';
import '../styles/subscriptions.scss';
import '../components/CWModal.scss';
import './FleetManagement.scss';

type FleetStatus = 'pending' | 'approved' | 'rejected';

interface FleetAccountItem {
  id: number;
  username: string;
  phoneNumber: string;
  email: string;
  businessName: string;
  address: string;
  vehicleCount: number;
  vehicles: CarInfo[];
  status: FleetStatus;
}

const PLACEHOLDER_VEHICLES: CarInfo[] = [
  { carId: 1, licensePlate: 'Oxt334', carMake: 'Dodge', carModel: '35000', carColor: 'Granite' },
  { carId: 2, licensePlate: 'Oxt335', carMake: 'Dodge', carModel: '35000', carColor: 'Granite' },
  { carId: 3, licensePlate: 'Oxt336', carMake: 'Ford', carModel: 'F-150', carColor: 'White' },
  { carId: 4, licensePlate: 'Oxt337', carMake: 'Chevrolet', carModel: 'Silverado', carColor: 'Black' },
  { carId: 5, licensePlate: 'Oxt338', carMake: 'Toyota', carModel: 'Tundra', carColor: 'Silver' },
];

const PLACEHOLDER_FLEETS: FleetAccountItem[] = [
  {
    id: 1,
    username: 'Username',
    phoneNumber: '(755) 294-247',
    email: 'lynch@example.com',
    businessName: 'Business Name',
    address: '5849 E pecan St',
    vehicleCount: PLACEHOLDER_VEHICLES.length,
    vehicles: PLACEHOLDER_VEHICLES,
    status: 'pending',
  },
  {
    id: 2,
    username: 'Username',
    phoneNumber: '(755) 294-247',
    email: 'lynch@example.com',
    businessName: 'Business Name',
    address: '5849 E pecan St',
    vehicleCount: PLACEHOLDER_VEHICLES.length,
    vehicles: PLACEHOLDER_VEHICLES,
    status: 'pending',
  },
  {
    id: 3,
    username: 'Username',
    phoneNumber: '(755) 294-247',
    email: 'lynch@example.com',
    businessName: 'Business Name',
    address: '5849 E pecan St',
    vehicleCount: PLACEHOLDER_VEHICLES.length,
    vehicles: PLACEHOLDER_VEHICLES,
    status: 'pending',
  },
  {
    id: 4,
    username: 'Username',
    phoneNumber: '(755) 294-247',
    email: 'lynch@example.com',
    businessName: 'Business Name',
    address: '5849 E pecan St',
    vehicleCount: PLACEHOLDER_VEHICLES.length,
    vehicles: PLACEHOLDER_VEHICLES,
    status: 'pending',
  },
  {
    id: 5,
    username: 'Username',
    phoneNumber: '(755) 294-247',
    email: 'lynch@example.com',
    businessName: 'Business Name',
    address: '5849 E pecan St',
    vehicleCount: PLACEHOLDER_VEHICLES.length,
    vehicles: PLACEHOLDER_VEHICLES,
    status: 'pending',
  },
];

const FleetManagement = () => {
  const { updateData } = useOutletContext<AppContextType>();
  const toaster = new MessageToaster();
  const [selectedTab, setSelectedTab] = useState('pending');
  const [loading] = useState(false);
  const [fleets, setFleets] = useState<FleetAccountItem[]>(PLACEHOLDER_FLEETS);
  const [selectedFleet, setSelectedFleet] = useState<FleetAccountItem | null>(null);
  const [showCarInfo, setShowCarInfo] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    updateData({
      heading: 'Fleet Management',
      url: `/${pageURLs.carwash_locations}`,
      showBackButton: true,
      showMainMenu: true,
      activeLink: 'fleet-management',
    });
  }, []);

  const visibleFleets = fleets.filter((item) =>
    selectedTab === 'approved' ? item.status === 'approved' : item.status === 'pending'
  );

  const openCarInfo = (fleet: FleetAccountItem) => {
    setSelectedFleet(fleet);
    setShowCarInfo(true);
  };

  const closeCarInfo = () => {
    setShowCarInfo(false);
    setSelectedFleet(null);
  };

  const openConfirm = (fleet: FleetAccountItem, action: 'approve' | 'reject') => {
    setSelectedFleet(fleet);
    setConfirmAction(action);
  };

  const closeConfirm = () => {
    setConfirmAction(null);
    setSelectedFleet(null);
  };

  const applyStatus = () => {
    if (!selectedFleet || !confirmAction) {
      return;
    }
    const nextStatus: FleetStatus = confirmAction === 'approve' ? 'approved' : 'rejected';
    setFleets((current) =>
      current.map((item) => (item.id === selectedFleet.id ? { ...item, status: nextStatus } : item))
    );
    toaster.toastSuccess(
      confirmAction === 'approve' ? 'Fleet account approved.' : 'Fleet account rejected.'
    );
    closeConfirm();
  };

  return (
    <div className="manage-subscription-container fleet-management-page">
      <Container>
        <div className="cw-main-tab">
          <Tabs
            activeKey={selectedTab}
            id="fleet-management-tab"
            className="cw-tab-title justify-content-end"
            onSelect={(key) => setSelectedTab(key || 'pending')}
          >
            <Tab eventKey="pending" title="Pending Approval">
              <div className="cw-table-wrap">
                <Table responsive className="m-0">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Phone Number</th>
                      <th>Email ID</th>
                      <th>Business Name</th>
                      <th>Address</th>
                      <th>No. of vehicles</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFleets.length ? (
                      visibleFleets.map((item) => (
                        <tr key={item.id}>
                          <td>{item.username}</td>
                          <td>{item.phoneNumber}</td>
                          <td>{item.email}</td>
                          <td>{item.businessName}</td>
                          <td>{item.address}</td>
                          <td>{item.vehicleCount}</td>
                          <td className="nowrap">
                            <div className="fleet-action-group d-flex align-items-center">
                              <Button
                                variant="outline-primary"
                                className="fleet-action-btn view-btn"
                                onClick={() => openCarInfo(item)}
                              >
                                View Car Info
                              </Button>
                              <Button
                                variant="outline-success"
                                className="fleet-action-btn approve-btn"
                                onClick={() => openConfirm(item, 'approve')}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outline-danger"
                                className="fleet-action-btn reject-btn"
                                onClick={() => openConfirm(item, 'reject')}
                              >
                                Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td style={{ textAlign: 'center' }} colSpan={7}>
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Tab>
            <Tab eventKey="approved" title="Approved Fleet">
              <div className="cw-table-wrap">
                <Table responsive className="m-0">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Phone Number</th>
                      <th>Email ID</th>
                      <th>Business Name</th>
                      <th>Address</th>
                      <th>No. of vehicles</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFleets.length ? (
                      visibleFleets.map((item) => (
                        <tr key={item.id}>
                          <td>{item.username}</td>
                          <td>{item.phoneNumber}</td>
                          <td>{item.email}</td>
                          <td>{item.businessName}</td>
                          <td>{item.address}</td>
                          <td>{item.vehicleCount}</td>
                          <td className="nowrap">
                            <Button
                              variant="outline-primary"
                              className="fleet-action-btn view-btn"
                              onClick={() => openCarInfo(item)}
                            >
                              View Car Info
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td style={{ textAlign: 'center' }} colSpan={7}>
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Tab>
          </Tabs>
        </div>
      </Container>

      <Modal show={showCarInfo} onHide={closeCarInfo} centered className="fleet-carinfo-modal">
        <Modal.Body>
          <h5 className="fleet-carinfo-title f-bold">Car Info</h5>
          {selectedFleet?.vehicles?.length ? (
            <Accordion key={selectedFleet.id} defaultActiveKey="0" className="fleet-carinfo-accordion">
              {selectedFleet.vehicles.map((car, index) => (
                <Accordion.Item key={car.carId ?? index} eventKey={String(index)}>
                  <Accordion.Header>{car.licensePlate}</Accordion.Header>
                  <Accordion.Body>
                    <div className="car-detail-row">
                      <span>Make</span>
                      <span className="car-detail-value">{car.carMake}</span>
                    </div>
                    <div className="car-detail-row">
                      <span>Model</span>
                      <span className="car-detail-value">{car.carModel}</span>
                    </div>
                    <div className="car-detail-row">
                      <span>Color</span>
                      <span className="car-detail-value">{car.carColor}</span>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            <p className="mb-0 text-muted">No car information found.</p>
          )}
          <div className="fleet-carinfo-actions">
            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={closeCarInfo}>
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(confirmAction)} onHide={closeConfirm} centered className="add-modal">
        <Modal.Body className="text-center">
          <h5 className="f-bold">
            {confirmAction === 'approve'
              ? 'Are you sure you want to approve this fleet account?'
              : 'Are you sure you want to reject this fleet account?'}
          </h5>
          <div className="btn-sec d-grid">
            <Button title="Cancel" variant="light" className="secondary-btn" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button title="Yes" variant="primary" className="primary-btn btn-main m-0" onClick={applyStatus}>
              Yes
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {loading && <SmallLoader />}
    </div>
  );
};

export default FleetManagement;
